/**
 * User-specific utility functions
 * Centralized user formatting and calculation functions
 */

import moment from 'moment';
import { toJsDate } from './dateUtils';

/**
 * Format user full name
 * @param {Object} user - User object with name and surname
 * @returns {string} Formatted full name
 */
export const formatUserFullName = (user) => {
  if (!user) return 'N/A';
  const name = user.name || '';
  const surname = user.surname || '';
  return `${name} ${surname}`.trim() || 'N/A';
};

/**
 * Format user creation date
 * @param {Object} user - User object with createDateTime or createdate
 * @returns {string} Formatted date string
 */
export const formatUserCreateDate = (user) => {
  if (!user) return 'N/A';
  
  if (user.createDateTime) {
    const d = toJsDate(user.createDateTime);
    return d ? moment(d).format('DD/MM/YY HH:mm') : 'N/A';
  } else if (user.createdate) {
    // String date
    return moment(user.createdate, 'YYYY-MM-DD').format('LL');
  }
  
  return 'N/A';
};

/**
 * Format user gender display
 * @param {string} gender - User gender
 * @returns {string} Formatted gender display
 */
export const formatUserGender = (gender) => {
  const genderMap = {
    male: 'M',
    female: 'F',
    prefernottosay: 'PNTS',
  };
  return genderMap[gender] || 'N/A';
};

/**
 * Format user role display
 * @param {string} role - User role
 * @returns {string} Formatted role display
 */
export const formatUserRole = (role) => {
  const roleMap = {
    admin: 'Admin',
    organisationCompanyManager: 'Org Manager',
    venueManager: 'Venue Manager',
    affiliate: 'Affiliate',
    operator: 'Operator',
    editor: 'Editor',
  };
  return roleMap[role] || 'User';
};

/**
 * Get user verification status
 * @param {Object} user - User object
 * @returns {Object} Verification status with display info
 */
export const getUserVerificationStatus = (user) => {
  if (!user) return { isVerified: false, display: 'N/A', color: 'grey' };
  
  const isVerified = user.isVerified === true;
  return {
    isVerified,
    display: isVerified ? 'Yes' : 'No',
    color: isVerified ? 'green' : 'red',
  };
};

/**
 * Get user's organization/venue name based on role
 * @param {Object} user - User object with crole and rid
 * @returns {Object} Organization info
 */
export const getUserOrganization = (user) => {
  if (!user || !user.rid) {
    return { reference: null, id: null, field: 'name', display: 'N/A' };
  }

  const orgMap = {
    organisationCompanyManager: {
      reference: 'organisationCompanies',
      field: 'name',
    },
    venueManager: {
      reference: 'venues',
      field: 'name',
    },
  };

  const orgInfo = orgMap[user.crole];
  if (!orgInfo) {
    return { reference: null, id: null, field: 'name', display: 'N/A' };
  }

  return {
    reference: orgInfo.reference,
    id: user.rid,
    field: orgInfo.field,
    display: 'Loading...', // Will be replaced by DynamicCell
  };
};

/**
 * Generate user action URLs
 * @param {string} userId - User ID
 * @param {string} action - Action type ('edit', 'sales')
 * @returns {string} Action URL
 */
export const getUserActionUrl = (userId, action) => {
  const baseUrl = '/#/users';
  const actionUrls = {
    edit: `${baseUrl}/${userId}`,
    sales: `/#/sales?userID=${userId}`,
  };
  return actionUrls[action] || baseUrl;
};

/**
 * Validate user search filters
 * @param {Object} filters - Filter object (`q`, or legacy `email` / `phone`)
 * @returns {Object} Validation result
 */
export const validateUserFilters = (filters = {}) => {
  const errors = {};
  const q = (filters.q || filters.email || filters.phone || '').trim();

  if (q && q.length < 2) {
    errors.q = 'Search must be at least 2 characters';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export default {
  formatUserFullName,
  formatUserCreateDate,
  formatUserGender,
  formatUserRole,
  getUserVerificationStatus,
  getUserOrganization,
  getUserActionUrl,
  validateUserFilters,
};
